import { DatePicker } from "@mantine/dates";
import { TextInput, Chip, Select, Checkbox, NumberInput } from "@mantine/core";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../components/pageList";
import { IconSettings, IconPhone } from "@tabler/icons";
import FormConfirmation from "../components/formConfirmation";
import RemoteSelect2 from "../components/remoteSelect2";
import ConsultantList from "../components/consultantList";
import Chips from "../components/chips";
import {
  DATA_COUNTRY,
  DATA_EDUCATION_LEVEL,
  DATA_LANGUAGE,
  DATA_NATIONALITY,
  DATA_SALUATATION,
  DATA_ID_TYPE,
  DATA_CLIENT_TYPES,
  DATA_CLIENT_STATUS,
  DATA_CLIENT_DISPOSAL_STATUS,
  DATA_GENDERS,
  DATA_MARIAGE_STATUS,
  DATA_OCCUPATIONS,
} from "../data/options";
import AddressList from "../components/addressList";
import PhoneNumberList from "../components/phoneNumberList";
import EmailList from "../components/emailList";
import ContactList from "../components/contactList";
import ClientIdentityList from "../components/clientIdentityList";
import ClientPiDatePicker from "../components/clientPiDatePicker";
import OccupationSelect from "../components/occupationSelect";
import {
  buildClientUpdateValidate,
  CLIENT_CONFIRM_FIELD_LABELS,
} from "./clientFormValidate";
import { applyDefaultIdIssueCountry } from "./clientIdIssueCountry";
import {
  isIdExpiryRequired,
  isIdentityDocumentExpired,
  isMainIdRequired,
  isOtherIdsSectionVisible,
  getClientIdPlaceholder,
} from "./clientIdentity";
import NoIdProvidedCheckbox from "../components/noIdProvidedCheckbox";
import ClientIdentityExpiredAlert from "../components/clientIdentityExpiredAlert";
import { withClientDueDiligence } from "./clientDueDiligenceForm";

export const getClientUpdateFormSchema = (
  t,
  { canChangeClientStatus = false, client = {} } = {},
) => {
  const clientLabel = [client.code, client.name].filter(Boolean).join(" — ");

  const schema = {
    title: "Update Client",
    ...(client?.name ? { titleField: "name" } : {}),
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px",
    },
    closeAfterSave: true,
    showSaveBar: false,
    apiEntity: "client",
    initialValues: {
      idType: "HKID",
      idIssueCountry: "Hong Kong",
      placeOfIncorporation: "Hong Kong",
      otherIds: [],
      extraFieldValues: {},
    },
    validate: buildClientUpdateValidate(t),
    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Type",
            orientation: "vertical",
            style: { minHeight: "400px" },
          },
          steps: [
            { key: "t1-1", label: "Type", icon: IconSettings },

            {
              key: "t1-2",
              label: "Client ID",
              icon: IconSettings,
              warn: isIdentityDocumentExpired,
            },
            { key: "t1-3", label: "Basic Info", icon: IconSettings },
            {
              key: "t1-pi",
              label: "Professional Investor",
              icon: IconSettings,
            },
            { key: "t1-6", label: "Contact Info", icon: IconPhone },
            { key: "t1-4", label: "Consultant", icon: IconSettings },

            { key: "t1-5", label: "Confirm", icon: IconSettings },
          ],
        },
        {
          key: "section-type",
          parent: "t1-1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client Types",
            description: "Please choose type for client",
            withBorder: false,
            ml: 10,
            mt: 0,
          },
        },

        {
          key: "section-ID",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Main ID",
            titleOrder: 5,
            description: "Please enter the primary identity document",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-other-ids",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: isOtherIdsSectionVisible,
          props: {
            title: "Other IDs",
            titleOrder: 5,
            description: "Add additional identity documents for this client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-basicInfo",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.type != "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-basicInfo-corporate",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.type == "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "basicInfo-g",
          parent: "section-basicInfo",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            { key: "bi-status", props: { span: 6 } },
            { key: "bi-disposal", props: { span: 6 } },
            { key: "bi-salutation", props: { span: 6 } },
            { key: "bi-gender", props: { span: 6 } },
            { key: "bi-surname", props: { span: 6 } },
            { key: "bi-given", props: { span: 6 } },
            { key: "bi-cname", props: { span: 6 } },
            { key: "bi-language", props: { span: 6 } },
            { key: "bi-birthday", props: { span: 6 } },
            { key: "bi-marital", props: { span: 6 } },
            { key: "bi-nationality", props: { span: 6 } },
            { key: "bi-birthplace", props: { span: 6 } },
            { key: "bi-residence", props: { span: 6 } },
            { key: "bi-education", props: { span: 6 } },
            { key: "bi-occupation", props: { span: 6 } },
            { key: "bi-salary", props: { span: 6 } },
            { key: "bi-account-close", props: { span: 6 } },
          ],
        },
        {
          key: "basicInfo-corporate-g",
          parent: "section-basicInfo-corporate",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            { key: "bic-status", props: { span: 6 } },
            { key: "bic-disposal", props: { span: 6 } },
            { key: "bic-name", props: { span: 6 } },
            { key: "bic-cname", props: { span: 6 } },
            { key: "bic-contact", props: { span: 6 } },
            { key: "bic-parent", props: { span: 6 } },
            { key: "bic-incorporation", props: { span: 6 } },
            { key: "bic-account-close", props: { span: 6 } },
          ],
        },

        {
          key: "section-pi",
          parent: "t1-pi",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Professional Investor",
            titleOrder: 5,
            description:
              "Record professional investor status, related dates, and knowledge of derivatives",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-consultant",
          parent: "t1-4",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Consultant",
            titleOrder: 5,
            description: "Please assign the corresponding consultant",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-confirm",
          parent: "t1-5",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Confirm",
            titleOrder: 3,
            description: clientLabel
              ? t(
                  "You are updating existing client {{client}}. Please confirm to save changes or go back to revise.",
                  { client: clientLabel }
                )
              : "You are updating an existing client. Please confirm to save changes or go back to revise.",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "tab-contact-wizard",
          parent: "t1-6",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            size: "sm",
            defaultValue: "Phone Numbers",
          },
          tab: [
            {
              key: "tab-wizard-phones",
              label: "Phone Numbers",
            },
            {
              key: "tab-wizard-emails",
              label: "Emails",
            },
            {
              key: "tab-wizard-addresses",
              label: "Address",
            },
            {
              key: "tab-wizard-related-persons",
              label: "Related Person Info",
            },
          ],
        },
      ],

      fields: [
        {
          name: "type",
          component: Chips,
          parent: "section-type",
          props: {
            required: true,
            labelOrder: 5,
            description: "Please Select the type of client.",
            items: DATA_CLIENT_TYPES,
            defaultValue: "INDIVIDUAL",
            multiple: false,
          },
        },
        {
          name: "identityExpiredAlert",
          component: ClientIdentityExpiredAlert,
          parent: "section-ID",
          visibleOnly: isIdentityDocumentExpired,
          props: { mb: 0 },
        },
        {
          name: "noIdProvided",
          component: NoIdProvidedCheckbox,
          parent: "section-ID",
          props: {
            label: "No ID provided",
            type: "checkbox",
          },
        },
        {
          name: "idType",
          component: Select,
          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "ID Type",
            placeholder: "",
            data: DATA_ID_TYPE,
            defaultValue: "HKID",
            searchable: true,
            clearable: true,
            onDateChange: (value, form) =>
              applyDefaultIdIssueCountry(value, form),
          },
        },
        {
          name: "id",
          component: TextInput,
          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "ID No.",
            placeholder: (formValues) =>
              getClientIdPlaceholder(formValues.idType),
          },
        },

        {
          name: "idIssueCountry",
          component: Select,

          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "Issue Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },
        {
          name: "idDocExpiryDate",
          component: DatePicker,
          type: "datePicker",
          parent: "section-ID",
          visibleOnly: (formValues) =>
            isMainIdRequired(formValues.idType) && formValues.idType !== "HKID",
          props: {
            required: (formValues) => isIdExpiryRequired(formValues.idType),
            label: "Expiry",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "otherIds",
          component: ClientIdentityList,
          parent: "section-other-ids",
          props: {
            hideTitle: true,
          },
        },
        {
          name: "status",
          component: Select,
          parent: "bi-status",
          props: {
            label: "Status",
            data: DATA_CLIENT_STATUS,
            required: true,
            readOnly: !canChangeClientStatus,
            searchable: true,
          },
        },
        {
          name: "disposalStatus",
          component: Select,
          parent: "bi-disposal",
          props: {
            label: "Disposal Status",
            data: DATA_CLIENT_DISPOSAL_STATUS,
            readOnly: true,
          },
        },
        {
          name: "status",
          component: Select,
          parent: "bic-status",
          props: {
            label: "Status",
            data: DATA_CLIENT_STATUS,
            required: true,
            readOnly: !canChangeClientStatus,
            searchable: true,
          },
        },
        {
          name: "disposalStatus",
          component: Select,
          parent: "bic-disposal",
          props: {
            label: "Disposal Status",
            data: DATA_CLIENT_DISPOSAL_STATUS,
            readOnly: true,
          },
        },
        {
          name: "salutation",
          component: Select,
          parent: "bi-salutation",
          props: {
            required: true,
            label: "Saluation",
            placeholder: "",
            data: DATA_SALUATATION,
            defaultValue: "Mr.",
            searchable: true,
          },
        },
        {
          name: "surname",
          component: TextInput,
          parent: "bi-surname",
          props: {
            required: true,
            placeholder: "Chan / Au Yeung",
            label: "Surname",
          },
        },
        {
          name: "givenName",
          component: TextInput,
          parent: "bi-given",
          props: {
            required: true,
            placeholder: "Tai Man / Man Tat",
            label: "Given Name",
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "bic-name",
          props: {
            required: true,
            placeholder: "Company ABC Ltd",
            label: "English Name (Company Name)",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "bi-cname",
          props: {
            placeholder: "陳大文",
            label: "Chinese Name",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "bic-cname",
          props: {
            placeholder: "大大有限公司",
            label: "Chinese Name (Company Name)",
          },
        },
        {
          name: "birthday",
          component: DatePicker,
          type: "datePicker",
          parent: "bi-birthday",
          props: {
            label: "Date of Birth",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "phones",
          component: PhoneNumberList,
          parent: "tab-wizard-phones",
          props: {},
        },
        {
          name: "emails",
          component: EmailList,
          parent: "tab-wizard-emails",
          props: {},
        },
        {
          name: "addresses",
          component: AddressList,
          parent: "tab-wizard-addresses",
          props: {},
        },
        {
          name: "contacts",
          component: ContactList,
          parent: "tab-wizard-related-persons",
          props: {
            phoneField: "tel",
          },
        },
        {
          name: "contactPerson",
          component: TextInput,
          parent: "bic-contact",
          props: {
            placeholder: "Kenny Chan",
            label: "Contact Person",
          },
        },
        {
          name: "parentCompany",
          component: RemoteSelect2,
          parent: "bic-parent",
          props: {
            label: "Parent Company",
            apiEntity: "client",
            required: true,
            placeholder: "Search Client By Name /BR ",
            valueField: "_id",
            labelField: "name",
            allowClear: true,
            labelRender: (data) =>
              `[ ${data.code ?? data.code ?? " -- "} ] ${data.name} ${
                data.cname ? `(${data.cname})` : ""
              } `,
            multiple: false,
            preQuery: { type: "CORPORATE" },
            searchFields: ["name", "id", "cname", "code"],
            clearable: true,
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "placeOfIncorporation",
          component: Select,
          parent: "bic-incorporation",
          props: {
            required: true,
            label: "Place of Incorporation",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "gender",
          component: Select,
          parent: "bi-gender",
          props: {
            required: true,
            label: "Gender",
            placeholder: "",
            data: DATA_GENDERS,
            defaultValue: "Male",
            searchable: true,
          },
        },

        {
          name: "occupation",
          component: OccupationSelect,
          parent: "bi-occupation",
          props: {
            required: true,
            placeholder: "Teacher",
            label: "Occupation",
            data: DATA_OCCUPATIONS,
          },
        },
        {
          name: "yearlySalary",
          component: NumberInput,
          parent: "bi-salary",
          props: {
            label: "Year Salary",
            placeholder: "0",
            min: 0,
            hideControls: true,
          },
        },
        {
          name: "educationLevel",
          component: Select,
          parent: "bi-education",
          props: {
            label: "Education Level",
            placeholder: "",
            data: DATA_EDUCATION_LEVEL,
            searchable: true,
            clearable: true,
          },
        },
        {
          name: "language",
          component: Select,
          parent: "bi-language",
          props: {
            required: true,
            label: "Language",
            placeholder: "",
            data: DATA_LANGUAGE,
            defaultValue: "CANTONESE",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "maritalStatus",
          component: Select,
          parent: "bi-marital",
          props: {
            required: true,
            label: "Marital Status",
            placeholder: "",
            data: DATA_MARIAGE_STATUS,
            defaultValue: "Single",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "nationality",
          component: Select,

          parent: "bi-nationality",
          props: {
            required: true,
            label: "Nationality",
            placeholder: "",
            data: DATA_NATIONALITY,
            defaultValue: "Chinese",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "residentialCountry",
          component: Select,

          parent: "bi-residence",
          props: {
            required: true,
            label: "Residential Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "placeOfBirth",
          component: Select,
          parent: "bi-birthplace",
          props: {
            required: true,
            label: "Country of Birth",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },
        {
          name: "accountCloseDate",
          component: DatePicker,
          type: "datePicker",
          parent: "bi-account-close",
          props: {
            label: "Account Close Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
            clearable: true,
          },
        },
        {
          name: "accountCloseDate",
          component: DatePicker,
          type: "datePicker",
          parent: "bic-account-close",
          props: {
            label: "Account Close Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
            clearable: true,
          },
        },

        {
          name: "knowledgeOfDerivatives",
          component: Checkbox,
          parent: "section-pi",
          props: {
            label: "Investor with Knowledge of Derivatives",
            type: "checkbox",
            mb: 20,
          },
        },
        {
          name: "professionalInvestor",
          component: Checkbox,
          parent: "section-pi",
          props: {
            label: "Professional Investor",
            type: "checkbox",
            mb: 20,
          },
        },
        {
          name: "piEffectiveDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Effective Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "piAnnualConfirmationDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Annual Confirmation Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "piExpirationReminderDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Expiration Reminder Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "piTerminationDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Termination Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "consultants",
          component: RemoteSelect2,
          parent: "section-consultant",
          props: {
            apiEntity: "user",
            required: true,
            // label: "Consultant",
            placeholder: "Assign consultant(s)",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) =>
              `${data.name ?? ""}${data.aliasName ? `,${data.aliasName}` : ""}${
                data.cname ? ` ${data.cname}` : ""
              } (${data.staffId ?? " -- "})`.trim(),
            multiple: true,
            // preQuery,
            // preQuery:  {isConsultant : true} ,
            searchFields: ["name", "staffId", "cname", "email", "aliasName"],
            sort: {
              by: "name",
              order: "desc",
            },
          },
        },

        {
          name: "consultantList",
          component: ConsultantList,
          parent: "section-consultant",
          props: {
            field: "consultants",
            mt: 0,
          },
        },

        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {
            validateOnMount: true,
            fieldLabels: CLIENT_CONFIRM_FIELD_LABELS,
            successText: clientLabel
              ? [
                  t("You are updating existing client {{client}}.", {
                    client: clientLabel,
                  }),
                  "Please click Confirm to save changes.",
                ]
              : [
                  "You are updating an existing client record.",
                  "Please click Confirm to save changes.",
                ],
          },
        },
      ],
    },
  };
  return withClientDueDiligence(schema, { t, required: false });
};
